/**
 * One list page for Contacten and Organisaties (spec-63 E2, design §4.2): the view tabs, the filter
 * bar with the quick filters, the active filters, the table with its sort, columns and selection,
 * the cards on a phone, the pages, and the bulk bar. The pages differ in what they show per column
 * and per row, and in a few extras; they say so in a `RecordListConfig`.
 */
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { ErrorCard } from '@/components/ErrorCard'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import { DataTable, type DataTableColumn } from '@/components/patterns/DataTable'
import { Pagination } from '@/components/Pagination'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/features/preinvest/components/PageHeader'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { cn } from '@/lib/utils'
import { todayIso } from '../dates'
import { ActiveFilters } from '../filters/ActiveFilters'
import { AdvancedFilterSheet } from '../filters/AdvancedFilterSheet'
import { ChangedChipRow } from '../filters/ChangedChipRow'
import { FilterBar } from '../filters/FilterBar'
import { CrmApiError } from '../http'
import { countRecords, exportRecords, runBulk } from '../query/api'
import { sameFilter } from '../query/codec'
import type { BulkResult, Condition, CrmObject, ExportBody, OptionDef, Filter, QueryBody, SearchBody, SearchPage } from '../query/types'
import { SaveAsListDialog } from '../lists/SaveAsListDialog'
import { DEFAULT_VIEW } from '../query/urlState'
import { AllViewsDialog } from '../views/AllViewsDialog'
import { SaveViewDialog } from '../views/SaveViewDialog'
import { ViewChangedBar } from '../views/ViewChangedBar'
import { ViewMenu } from '../views/ViewMenu'
import { ViewSelect } from '../views/ViewSelect'
import { ViewTabs } from '../views/ViewTabs'
import { BulkActionBar } from './BulkActionBar'
import { BulkMenus, type BulkMenusProps, type BulkRequest } from './BulkMenus'
import { ColumnPickerSheet } from './ColumnPickerSheet'
import { MobileFilterSheet } from './MobileFilterSheet'
import { PAGE_SIZE, useRecordList, withLocked, type RecordList } from './useRecordList'

/** How a page shows one column of the register: its header and its cell. */
export interface ColumnRender<T> {
  header: string
  cell(row: T): ReactNode
  className?: string
}

export interface ExportExtras {
  /** A selection: the rows in the order of the query, limited to these. */
  ids?: string[]
  /** "Organisaties met contactpersonen": one row per current member. */
  variant?: NonNullable<ExportBody['variant']>
  /** Instead of the page's own file name. */
  filename?: string
}

/** What `headerActions` needs to export the list as it is on screen. */
export interface ExportTools {
  exporting: boolean
  exportRows(extras?: ExportExtras): Promise<void>
}

export interface RecordListConfig<T> {
  object: CrmObject
  title: string
  subtitle: string
  searchPlaceholder: string
  searchLabel: string
  /** "9 contacten", "1 contact". */
  noun(count: number): string
  rowKey(row: T): string
  /** The name of a row, for the accessible name of its checkbox. */
  rowLabel(row: T): string
  /** The cell of every column the register offers, by key. A key without one is not shown. */
  columns: Record<string, ColumnRender<T>>
  /** The rows as cards, from 768 px down. `sort` decides which moment the card shows. */
  cardList(rows: T[], sort: string): ReactNode
  /** The choices of "Sorteren" in the filter sheet on a phone. */
  sortOptions: readonly OptionDef[]
  headerActions(list: RecordList<T>, tools: ExportTools): ReactNode
  /** Icon buttons at the end of a row. */
  rowActions?(row: T, list: RecordList<T>): ReactNode
  /** Columns after those of the register that exist only on this page (Bron and Toegevoegd on a list's members). */
  extraColumns?: { key: string; column: ColumnRender<T>; sortable?: boolean }[]
  /** Quick conditions that always hold (the list a members page belongs to); not in the URL, no chip to remove. */
  locked?: Condition[]
  /** What says that the page is locked, shown in the line of active filters ("Lijst: Slush 2026"). */
  lockedChips?: string[]
  /** Fields in every search and export body (`list_context`, `member_sources`). */
  extraBody?: Pick<SearchBody, 'list_context' | 'member_sources'>
  /** Changes when the page around the list changed what it shows. */
  reloadToken?: number
  /** `false`: this page sits inside another one and does not open or remember a last used view. */
  rememberView?: boolean
  /** No page header: the list is a part of another page. */
  hideHeader?: boolean
  /** Rows that are gone on screen already (an undo is still possible). */
  hiddenKeys?: ReadonlySet<string>
  /** A column before the first one, e.g. the toggle of the contact persons. */
  leadingColumn?: DataTableColumn<T>
  /** Something for the table next to the filter bar that needs the rows on the page. Hidden from 768 px down, where the rows are cards. */
  toolbar?(rows: T[]): ReactNode
  /** A row under a row; `colSpan` is the width of the table. */
  renderAfterRow?(row: T, colSpan: number): ReactNode
  /** The bulk buttons when the page has its own (Afspraken); else those of contacts and organisations. */
  bulkMenus?(props: BulkMenusProps): ReactNode
  /** Fixed tabs instead of saved views (Lijsten, ED-40): no "Weergave", no "Alle weergaven", no "view changed" bar. */
  fixedTabs?: boolean
  /** `false`: no checkboxes and no bulk bar (Lijsten, ED-40). Default true. */
  selectable?: boolean
  /** Something between the page header and the tabs (the upcoming lists of Lijsten). */
  aboveTabs?: ReactNode
  /** Adds "Opslaan als lijst…" to the view menu (Contacten, design §5.7). */
  saveAsList?: boolean
  /** What a default view without a search or filter shows when there is nothing at all. */
  empty: ReactNode
  exportFilename: string
  /** Dialogs of the page (new contact, mail): they sit outside the card and may reload the list. */
  dialogs?(list: RecordList<T>): ReactNode
}

/**
 * The ticked rows. They belong to one list (query, sort and page) and to the rows it showed: another
 * list starts empty and what was ticked is gone for good; the same list loaded again (an undo, a new
 * task, "Opnieuw proberen") keeps only the ticks of the rows it still shows.
 */
interface Picked<T> {
  scope: string
  /** The answer the rows were ticked on. */
  data: SearchPage<T> | null
  ids: ReadonlySet<string>
  /** Every row that matches, not just the ticked ones: "Alle n selecteren". */
  all: boolean
}

/** What a bulk action works on: the ticked rows, or everything that matches with the total that was on screen. */
type BulkTarget = { ids: string[] } | { query: QueryBody; expected_total: number }

interface PendingBulk {
  open: boolean
  request: BulkRequest
  /** What the question is about: the answer acts on exactly that, also when the list reloads while it is open. */
  target: BulkTarget
  /** How many it is about, so the dialog does not change while it closes. */
  count: number
}

const NO_IDS: ReadonlySet<string> = new Set<string>()

/** "9 contacten" without its number: "contacten". */
const plural = (noun: (count: number) => string): string => noun(0).replace(/^\d+\s*/, '')

/** The view lists archived records: the status button of the bulk bar reactivates then. */
function listsArchived(filter: Filter): boolean {
  return filter.quick.some(
    (condition) =>
      condition.property === 'status' &&
      condition.operator === 'is_any_of' &&
      Array.isArray(condition.value) &&
      condition.value.length > 0 &&
      condition.value.every((value) => value === 'inactief'),
  )
}

const message = (caught: unknown, fallback: string): string => (caught instanceof Error ? caught.message : fallback)

export function RecordListPage<T>({ config }: { config: RecordListConfig<T> }) {
  const { object, noun, rowKey } = config
  const list = useRecordList<T>(object, {
    locked: config.locked,
    extraBody: config.extraBody,
    reloadToken: config.reloadToken,
    rememberView: config.rememberView,
    extraSortKeys: (config.extraColumns ?? []).filter((extra) => extra.sortable).map((extra) => extra.key),
  })
  const navigate = useNavigate()
  const selectable = config.selectable !== false
  const { register, views, view, state, data, options, params } = list
  const today = todayIso()
  const { hiddenKeys } = config
  const rows = (data?.items ?? []).filter((row) => !hiddenKeys?.has(rowKey(row)))

  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [columnsOpen, setColumnsOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [saveOpen, setSaveOpen] = useState(false)
  const [allViewsOpen, setAllViewsOpen] = useState(false)
  const [saveListOpen, setSaveListOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState<PendingBulk | null>(null)

  // The ticked rows belong to the list they were ticked in. Another query, sort or page starts empty
  // (the old ticks are dropped, not hidden: they must not come back when that list is shown again).
  // The same list loaded again keeps the ticks of the rows it still shows and drops the others the
  // same way: the bar never counts, and an action never starts on, a row that is not ticked on screen
  // (a question already open acts on what it asked about).
  const scope = JSON.stringify([list.query, state?.sort ?? null, state?.page ?? null])
  const [picked, setPicked] = useState<Picked<T>>({ scope, data, ids: NO_IDS, all: false })
  let selection = picked
  if (selection.scope !== scope) {
    selection = { scope, data, ids: NO_IDS, all: false }
  } else if (selection.data !== data) {
    const onScreen = new Set(rows.map(rowKey))
    const ids = new Set([...selection.ids].filter((id) => onScreen.has(id)))
    // "Alle n" rides on a ticked page: it stays as long as some of its rows are still ticked on screen.
    selection = { scope, data, ids, all: selection.all && ids.size > 0 }
  }
  if (selection !== picked) setPicked(selection)

  // While the next rows load, the old ones stay on screen, dimmed. A tick on them would be filed under
  // the new list (the URL has moved on) and then point at rows that are no longer there.
  const refreshing = list.refreshing

  function toggle(key: string) {
    if (refreshing) return
    const next = new Set(selection.ids)
    if (!next.delete(key)) next.add(key)
    setPicked({ scope, data, ids: next, all: false })
  }

  function toggleAll() {
    if (refreshing) return
    const everyone = rows.length > 0 && rows.every((row) => selection.ids.has(rowKey(row)))
    setPicked({ scope, data, ids: everyone ? NO_IDS : new Set(rows.map(rowKey)), all: false })
  }

  function selectAllMatching() {
    if (!refreshing) setPicked({ ...selection, all: true })
  }

  const clearSelection = () => setPicked({ scope, data, ids: NO_IDS, all: false })

  // ---- Export ----
  async function exportRows(extras: ExportExtras = {}): Promise<void> {
    if (!list.query || !state || exporting) return
    setExporting(true)
    try {
      const body: ExportBody = { query: list.query, sort: state.sort, columns: state.columns, ...config.extraBody }
      if (extras.ids) body.ids = extras.ids
      if (extras.variant) body.variant = extras.variant
      await exportRecords(object, body, extras.filename ?? config.exportFilename)
    } catch (caught) {
      toast.error(message(caught, 'Exporteren mislukt.'))
    } finally {
      setExporting(false)
    }
  }

  // ---- Bulk ----
  /** What happened, said once: an undo for delete and archive, a plain message for the rest. */
  function report(request: BulkRequest, result: BulkResult) {
    const skipped = result.skipped
    const reload = () => list.reload()
    if (result.updated === 0) {
      toast.info(skipped.length > 0 ? `${skipped.length} overgeslagen: ${skipped[0].reason}` : 'Er is niets gewijzigd.')
      return
    }
    if (request.action === 'delete') {
      toastUndo(`${noun(result.updated)} verwijderd`, () => runBulk(object, 'restore', { selection: { ids: result.ids } }).then(reload))
    } else if (request.action === 'status') {
      const archived = request.params.status === 'inactief'
      toastUndo(`${noun(result.updated)} ${archived ? 'gearchiveerd' : 'geheractiveerd'}`, () =>
        runBulk(object, 'status', { selection: { ids: result.ids }, status: archived ? 'actief' : 'inactief' }).then(reload),
      )
    } else {
      toast.success(`${noun(result.updated)} bijgewerkt.`)
    }
    if (skipped.length > 0) toast.info(`${skipped.length} overgeslagen: ${skipped[0].reason}`)
  }

  /** Runs a bulk action on `target`; false when it failed (the selection stays, the message says why). */
  async function execute(request: BulkRequest, target: BulkTarget): Promise<boolean> {
    if (busy) return false
    setBusy(true)
    try {
      const result = await runBulk(object, request.action, { selection: target, ...request.params })
      report(request, result)
      clearSelection()
      list.reload()
      return true
    } catch (caught) {
      toast.error(message(caught, 'De bulkactie is mislukt.'))
      // The selection no longer matches the list: show the list as it is now.
      if (caught instanceof CrmApiError && caught.status === 409) list.reload()
      return false
    } finally {
      setBusy(false)
    }
  }

  /** What is ticked on screen now becomes the target: at once, or once the question is answered. */
  function ask(request: BulkRequest) {
    if (!data || !list.query) return
    const target: BulkTarget = selection.all ? { query: list.query, expected_total: data.total } : { ids: [...selection.ids] }
    if (!request.confirm) {
      void execute(request, target)
      return
    }
    setPending({ open: true, request, target, count: selection.all ? data.total : selection.ids.size })
  }

  async function confirmPending() {
    if (!pending) return
    await execute(pending.request, pending.target)
    setPending((current) => (current ? { ...current, open: false } : current))
  }

  // ---- Views ----
  async function overwriteView() {
    setSaving(true)
    try {
      await list.saveView()
    } catch (caught) {
      toast.error(message(caught, 'Opslaan mislukt.'))
    } finally {
      setSaving(false)
    }
  }

  // ---- The page ----
  const header = config.hideHeader ? null : (
    <PageHeader title={config.title} subtitle={config.subtitle} actions={config.headerActions(list, { exporting, exportRows })} />
  )

  if (!register || !views || !view || !state) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        {list.failed ? (
          <ErrorCard onRetry={list.reload} />
        ) : (
          <div className="vonk-card flex flex-col gap-2" aria-hidden>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {config.dialogs?.(list)}
      </div>
    )
  }

  const tableColumns: DataTableColumn<T>[] = []
  if (config.leadingColumn) tableColumns.push(config.leadingColumn)
  for (const key of state.columns) {
    const render = config.columns[key]
    if (!render) continue
    const sortable = register.properties.find((property) => property.key === key)?.sortable === true
    tableColumns.push({ key, header: render.header, sortKey: sortable ? key : undefined, className: render.className, cell: render.cell })
  }
  for (const extra of config.extraColumns ?? []) {
    tableColumns.push({ key: extra.key, header: extra.column.header, sortKey: extra.sortable ? extra.key : undefined, className: extra.column.className, cell: extra.column.cell })
  }
  const { rowActions } = config
  if (rowActions) {
    tableColumns.push({
      key: 'acties',
      header: <span className="sr-only">Acties</span>,
      className: 'w-24',
      cell: (row) => <div className="flex items-center justify-end">{rowActions(row, list)}</div>,
    })
  }
  // The checkbox column and every column the table has.
  const colSpan = tableColumns.length + 1

  const selecting = selectable && selection.ids.size > 0
  const defaultAndUnfiltered = view.key === DEFAULT_VIEW && state.q === '' && sameFilter(state.filter, view.query)

  let results: ReactNode
  if (list.failed) {
    results = <ErrorCard onRetry={list.reload} />
  } else if (list.loading || data === null) {
    results = (
      <div className="flex flex-col gap-2" aria-hidden>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  } else if (rows.length === 0) {
    results = defaultAndUnfiltered ? (
      config.empty
    ) : (
      <p className="py-6 text-center text-sm text-muted-foreground">{`Geen ${plural(noun)} gevonden voor dit filter.`}</p>
    )
  } else {
    results = (
      <>
        <div className="hidden md:block">
          <DataTable
            caption={config.title}
            columns={tableColumns}
            rows={rows}
            rowKey={rowKey}
            sort={state.sort}
            onSortChange={list.setSort}
            refreshing={refreshing}
            stickyHeader
            selection={selectable ? { selected: selection.ids, onToggle: toggle, onToggleAll: toggleAll, label: config.rowLabel } : undefined}
            renderAfterRow={config.renderAfterRow ? (row) => config.renderAfterRow?.(row, colSpan) : undefined}
          />
        </div>
        <div className={cn('transition-opacity md:hidden', refreshing && 'opacity-60')} aria-busy={refreshing || undefined}>
          {config.cardList(rows, state.sort)}
        </div>
        {/* As C's lists: page numbers only when there is more than one page (the filter bar says how many there are). */}
        {data.total > PAGE_SIZE || state.page > 1 ? (
          <Pagination page={state.page} pageSize={PAGE_SIZE} total={data.total} onPage={list.setPage} />
        ) : null}
      </>
    )
  }

  const bulkProps: BulkMenusProps = {
    register,
    options,
    archived: listsArchived(state.filter),
    onRequest: ask,
    onExport: () => void exportRows(selection.all ? {} : { ids: [...selection.ids] }),
    selected: { ids: [...selection.ids], all: selection.all, total: data?.total ?? 0 },
    onDone: () => {
      clearSelection()
      list.reload()
    },
  }
  const countDraft = (draft: Filter) => countRecords(object, { ...withLocked(draft, config.locked), ...(state.q ? { q: state.q } : {}) })
  const bulkTitle = pending
    ? pending.request.destructive
      ? `${noun(pending.count)} verwijderen?`
      : `${noun(pending.count)}: ${pending.request.summary}?`
    : ''

  return (
    <div className="flex flex-col gap-6">
      {header}
      {config.aboveTabs}

      <div className="flex flex-col gap-3">
        <ViewTabs
          data={views}
          currentKey={view.key}
          params={params}
          onNewView={config.fixedTabs ? undefined : () => setSaveOpen(true)}
          onAllViews={config.fixedTabs ? undefined : () => setAllViewsOpen(true)}
          onSaveAsList={config.saveAsList ? () => setSaveListOpen(true) : undefined}
        />
        <div className="flex items-center gap-2 md:hidden">
          <div className="min-w-0 flex-1">
            <ViewSelect data={views} currentKey={view.key} params={params} />
          </div>
          {config.saveAsList ? <ViewMenu onSaveAsList={() => setSaveListOpen(true)} className="max-md:h-11" /> : null}
          <Button type="button" variant="outline" className="max-md:h-11" onClick={() => setMobileOpen(true)}>
            Filters
          </Button>
        </div>

        <div className="vonk-card flex flex-col gap-4">
          {selecting && data ? (
            <BulkActionBar
              count={selection.ids.size}
              total={data.total}
              pageCount={rows.length}
              allMatching={selection.all}
              noun={noun}
              onSelectAll={selectAllMatching}
              onClear={clearSelection}
            >
              {config.bulkMenus ? config.bulkMenus(bulkProps) : <BulkMenus object={object} {...bulkProps} />}
            </BulkActionBar>
          ) : (
            <>
              <FilterBar
                register={register}
                filter={state.filter}
                q={state.q}
                sort={state.sort}
                total={data?.total ?? null}
                options={options}
                today={today}
                noun={noun}
                searchPlaceholder={config.searchPlaceholder}
                searchLabel={config.searchLabel}
                onSearch={list.setQ}
                onFilterChange={list.setFilter}
                onOpenAdvanced={() => setAdvancedOpen(true)}
                onOpenColumns={() => setColumnsOpen(true)}
              />
              {config.toolbar ? <div className="flex flex-wrap items-center gap-2 max-md:hidden">{config.toolbar(rows)}</div> : null}
            </>
          )}
          {config.lockedChips && config.lockedChips.length > 0 ? (
            <div role="group" aria-label="Vaste filters" className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Vast:</span>
              {config.lockedChips.map((text) => (
                <span key={text} className="inline-flex max-w-full items-center rounded-full bg-muted px-3 py-0.5 text-sm">
                  <span className="break-words md:truncate" title={text}>
                    {text}
                  </span>
                </span>
              ))}
            </div>
          ) : null}
          <ActiveFilters filter={state.filter} register={register} names={options.names} today={today} onChange={list.setFilter} />
          {state.modified && config.fixedTabs ? (
            <Button type="button" variant="ghost" size="sm" className="self-start" onClick={list.reset}>
              Herstellen
            </Button>
          ) : null}
          {state.modified && !config.fixedTabs ? (
            <ViewChangedBar view={view} busy={saving} onSave={() => void overwriteView()} onSaveAsNew={() => setSaveOpen(true)} onReset={list.reset} />
          ) : null}
          <ChangedChipRow filter={state.filter} today={today} onChange={list.setFilter} />
          {results}
        </div>
      </div>

      <AdvancedFilterSheet
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        register={register}
        filter={state.filter}
        options={options}
        today={today}
        noun={noun}
        count={countDraft}
        onApply={list.setFilter}
      />
      <ColumnPickerSheet open={columnsOpen} onOpenChange={setColumnsOpen} register={register} columns={state.columns} onApply={list.setColumns} />
      <MobileFilterSheet
        open={mobileOpen}
        onOpenChange={setMobileOpen}
        register={register}
        filter={state.filter}
        sort={state.sort}
        sortOptions={config.sortOptions}
        options={options}
        today={today}
        total={data?.total ?? null}
        noun={noun}
        onFilterChange={list.setFilter}
        onSortChange={list.setSort}
        onAdvanced={() => {
          setMobileOpen(false)
          setAdvancedOpen(true)
        }}
      />
      {config.fixedTabs ? null : (
        <>
          <SaveViewDialog open={saveOpen} onOpenChange={setSaveOpen} onSave={list.saveAsNew} />
          <AllViewsDialog
            open={allViewsOpen}
            onOpenChange={setAllViewsOpen}
            object={object}
            data={views}
            currentKey={view.key}
            onOpen={list.openView}
            onChanged={list.reloadViews}
          />
        </>
      )}
      {config.saveAsList ? (
        <SaveAsListDialog
          open={saveListOpen}
          onOpenChange={setSaveListOpen}
          query={list.query}
          total={data?.total ?? null}
          onCreated={(listId) => void navigate(`/relaties/lijsten/${listId}`)}
        />
      ) : null}
      <ConfirmDialog
        open={pending?.open ?? false}
        onOpenChange={(open) => {
          if (!open && !busy) setPending((current) => (current ? { ...current, open: false } : current))
        }}
        title={bulkTitle}
        description={pending?.request.destructive ? 'Ze verdwijnen ook uit lijsten. Direct daarna kun je dit ongedaan maken.' : pending?.request.note}
        confirmLabel={pending?.request.destructive ? 'Verwijderen' : 'Uitvoeren'}
        destructive={pending?.request.destructive ?? false}
        busy={busy}
        onConfirm={() => void confirmPending()}
      />
      {config.dialogs?.(list)}
    </div>
  )
}
